import { short } from '../decode.js'
import { lookupToken } from './tokenDecimals.js'

/**
 * Pure per-format-type renderers for ERC-7730 field values. Every formatter is
 * defensive: unexpected input falls back to formatRaw rather than throwing or
 * guessing - a mis-rendered field in a fund-custody review UI is worse than a
 * plain one. resolve.ts pre-resolves any '$.'-prefixed descriptor references
 * (tokens, thresholds, enum maps) before calling these, so formatters only ever
 * see concrete values.
 */

export function formatRaw(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'bigint') return value.toString()
  if (Array.isArray(value)) return `[${value.map((v) => formatRaw(v)).join(', ')}]`
  return String(value)
}

/** Known tokens render as `SYMBOL (0x1234…abcd)` so a reviewer sees what is
 *  being bought/sent, with the address kept for verification. */
export function formatAddressName(value: unknown): string {
  if (typeof value !== 'string') return formatRaw(value)
  const token = lookupToken(value)
  return token ? `${token.symbol} (${short(value)})` : short(value)
}

function toBigInt(value: unknown): bigint | null {
  if (typeof value === 'bigint') return value
  if (typeof value === 'number' && Number.isInteger(value)) return BigInt(value)
  if (typeof value === 'string' && /^-?\d+$/.test(value)) return BigInt(value)
  return null
}

function toDecimalString(value: bigint, decimals: number): string {
  const negative = value < 0n
  const abs = negative ? -value : value
  const divisor = 10n ** BigInt(decimals)
  const whole = abs / divisor
  const frac = abs % divisor
  const sign = negative ? '-' : ''
  if (frac === 0n) return `${sign}${whole}`
  const fracStr = frac.toString().padStart(decimals, '0').replace(/0+$/, '')
  return `${sign}${whole}.${fracStr}`
}

/** Fractional digits shown before a display amount is truncated. */
const MAX_DISPLAY_FRACTION_DIGITS = 6

/**
 * Human-facing decimal: thousands-grouped whole part, fraction truncated to
 * MAX_DISPLAY_FRACTION_DIGITS. A truncated value is prefixed with `~` so it
 * never reads as exact - this is a review UI, a silently shortened number
 * would misstate what is being signed.
 */
export function displayDecimal(value: bigint, decimals: number): string {
  const exact = toDecimalString(value, decimals)
  const negative = exact.startsWith('-')
  const [whole = '0', frac = ''] = (negative ? exact.slice(1) : exact).split('.')
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  const shown = frac.slice(0, MAX_DISPLAY_FRACTION_DIGITS).replace(/0+$/, '')
  const approx = frac.length > MAX_DISPLAY_FRACTION_DIGITS ? '~' : ''
  return `${approx}${negative ? '-' : ''}${grouped}${shown ? `.${shown}` : ''}`
}

export interface TokenAmountOptions {
  /** Threshold above which `message` is shown instead of the amount (the
   *  "unlimited approval" pattern), already resolved to a concrete bigint. */
  threshold?: bigint
  message?: string
}

/** `resolvedTokenAddress` is the already-resolved concrete token address (fixed
 *  or looked up via tokenPath in the decoded calldata) - null if it couldn't be
 *  determined, in which case this renders the raw integer rather than guessing. */
export function formatTokenAmount(
  rawValue: unknown,
  resolvedTokenAddress: string | null,
  opts: TokenAmountOptions = {},
): string {
  const amount = toBigInt(rawValue)
  if (amount === null) return formatRaw(rawValue)

  if (opts.threshold !== undefined && amount >= opts.threshold && opts.message) {
    return opts.message
  }

  const token = lookupToken(resolvedTokenAddress)
  if (!token) return formatRaw(rawValue)
  return `${displayDecimal(amount, token.decimals)} ${token.symbol}`
}

/** Native-currency amount (wei, 18 decimals) - the `amount` format has no params. */
export function formatAmount(rawValue: unknown): string {
  const value = toBigInt(rawValue)
  if (value === null) return formatRaw(rawValue)
  return `${displayDecimal(value, 18)} ETH`
}

export type DateEncoding = 'blockheight' | 'timestamp'

export function formatDate(rawValue: unknown, encoding: DateEncoding | undefined): string {
  const value = toBigInt(rawValue)
  if (value === null) return formatRaw(rawValue)
  if (encoding === 'timestamp') {
    const ms = Number(value) * 1000
    if (!Number.isFinite(ms)) return formatRaw(rawValue)
    const date = new Date(ms)
    if (Number.isNaN(date.getTime())) return formatRaw(rawValue)
    return date.toISOString().replace('T', ' ').replace(/\.\d+Z$/, ' UTC')
  }
  // 'blockheight' (or unrecognized encoding): no clock to convert against, show as-is.
  return `block ${value}`
}

export interface UnitOptions {
  base: string
  decimals?: number
  prefix?: boolean
}

const SI_PREFIXES: [threshold: bigint, symbol: string][] = [
  [10n ** 9n, 'G'],
  [10n ** 6n, 'M'],
  [10n ** 3n, 'k'],
]

export function formatUnit(rawValue: unknown, opts: UnitOptions | undefined): string {
  const value = toBigInt(rawValue)
  if (value === null || !opts?.base) return formatRaw(rawValue)
  if (opts.prefix) {
    const scaled = opts.decimals ? toDecimalString(value, opts.decimals) : value.toString()
    for (const [threshold, symbol] of SI_PREFIXES) {
      if (value >= threshold) {
        const divided = Number(scaled) / Number(threshold)
        return `${divided} ${symbol}${opts.base}`
      }
    }
  }
  return `${displayDecimal(value, opts.decimals ?? 0)} ${opts.base}`
}

export function formatEnum(rawValue: unknown, enumMap: Record<string, string> | undefined): string {
  if (!enumMap) return formatRaw(rawValue)
  const key = formatRaw(rawValue)
  return enumMap[key] ?? formatRaw(rawValue)
}
